import React, { useEffect, useState } from "react";
import { IEntityValueTankModel } from "../../common/models/IEntityValueModels";
import Highcharts, { Options } from "highcharts";
import { Box, Stack, Typography, useTheme } from "@mui/material";
import { StandaloneTankTATEnum } from "../../common/enums/StandaloneTankTATEnum";
import { useRouteContext } from "../../hooks/use-route-context/UseRouteContext";
import { getFeetAndInchesText } from "../../common/utilities/FeetAndInches";
import _ from "lodash";
import CompoundTankTagDisplayValue from "./CompoundTankTagDisplayValue";
import { getChartOptions, getTagDisplayText, getTankHeights, ITankTagDisplayValue } from "./CompoundTankUtils";
import { getTankDisplayStyleDefinition } from "../../common/models/TankDisplayStyleDefinition";

export type CompoundTankProps = {
    tank: IEntityValueTankModel;
    legendVertical?: boolean;
    designMode?: boolean;
    //dense?: boolean;
};

export default function CompoundTank({
    tank,
    legendVertical,
    designMode,
    //dense
}: CompoundTankProps) {

    const routeContext = useRouteContext();
    const theme = useTheme();
    const [options, setOptions] = useState<Options | undefined>();
    //const isPhone = isMobile && !isTablet;

    const chartID = designMode ? `${tank.id}-chart-dm` : `${tank.id}-chart`;
    const tankHeight = getFeetAndInchesText((tank.tankStyle.elevationInInches ?? 0) + tank.tankStyle.heightInInches);
    const tankDefinition = getTankDisplayStyleDefinition(tank.tankStyle.displayStyle);
    const { overflow } = getTankHeights(tank);

    useEffect(() => {
        const tankOptions = getChartOptions(tank, theme);

        if (!_.isEqual(options, tankOptions)) {
            setOptions(tankOptions);
        }
    }, [
        options,
        tank,
        setOptions,
        legendVertical,
        theme,
        routeContext.contextObject
    ]);

    const values: ITankTagDisplayValue[] = [
        getTagDisplayText(tank, StandaloneTankTATEnum.LiquidHeight)
    ];

    useEffect(() => {
        if (!options) {
            return;
        }

        const chart = Highcharts.chart(chartID, { ...options });
        chart.reflow();

        // container may not be laid out yet on first paint
        const timerID = window.setTimeout(() => chart.reflow(), 500);

        return () => {
            clearTimeout(timerID);
            chart.destroy();
        };
    }, [
        options,
        chartID
    ]);

    return (
        <Stack direction={'column'} spacing={2}>
            <Typography variant="body2"
                sx={{
                    marginLeft: 'auto',
                    marginRight: 'auto',
                    alignSelf: 'center',
                    flex: '0 0 auto',
                }}
            >
                {`Tank Height:  ${tankHeight}`}
            </Typography>
            <svg
                xmlns="http://www.w3.org/2000/svg"
                width="100%"
                height="100%"
                viewBox="0 0 1000 1000"
            >
                <foreignObject
                    x={tankDefinition.graphX}
                    y={tankDefinition.graphY}
                    width={tankDefinition.graphWidth}
                    height={tankDefinition.graphHeight}
                >
                    <Box id={chartID} sx={{ height: 1, width: 1 }}></Box>
                </foreignObject>

                <image
                    href={tankDefinition.imageURL}
                    x={tankDefinition.imageX}
                    y={tankDefinition.imageY}
                    width={tankDefinition.imageWidth}
                    height={tankDefinition.imageHeight}
                />
            </svg>
            <CompoundTankTagDisplayValue
                tagValues={values}
                overflow={overflow}
            />
        </Stack>
    );
}

